import {
  ALL_INSTRUMENTS,
  getInstrument,
  getSampleNotes,
  isSampledInstrument,
  isSustainedInstrument,
} from './midiInstruments';

// Downloading, decoding and caching the recordings behind the sampled instrument presets.
//
// Deliberately free of React and of Tone imports: Tone is handed in by the caller, which already
// loads it lazily, so pulling samples never drags the ~200KB library into a bundle that wasn't
// going to need it anyway.

// The FluidR3 GM set, one MP3 per note. Serves CORS-open with a week of cache-control, and the
// files are named with flats (Db4.mp3) — which is what getSampleNotes produces.
//
// This is the single line to change to serve the samples from our own origin instead: vendor the
// files under public/samples and point this at '/samples'.
const SOUNDFONT_BASE_URL = 'https://cdn.jsdelivr.net/gh/gleitz/midi-js-soundfonts@gh-pages/FluidR3_GM';

// Bump the suffix if the sample set or the note layout ever changes, so browsers holding the old
// files fetch the new ones rather than playing a half-updated instrument
const CACHE_NAME = 'midi-samples-v1';

// A same-origin marker written once an instrument has downloaded in full. Checking one key beats
// counting twenty-odd individual files every time the dropdown renders, and it is only written
// after the last note lands — so a download interrupted halfway never looks complete.
const COMPLETE_MARKER_PREFIX = '/__midi-samples__/';

// Enough to keep the connection busy without opening twenty-odd requests at once, which on a slow
// link makes every file finish late instead of letting the progress bar move steadily
const MAX_CONCURRENT_FETCHES = 6;

// Decoded and ready to play, keyed by instrument id. Holding the promise rather than the result
// means a second caller arriving mid-download waits on the first one's fetch instead of starting
// its own — which is exactly what happens when the user hits play while the bar is still moving.
const bufferCache = new Map();

// Ids whose download has actually finished. Separate from bufferCache, which holds an entry from
// the moment a download *starts* — reading completion off it would tell a modal reopened
// mid-download that the instrument was ready, and it would drop the progress bar and enable play.
const settledIds = new Set();

// How far each in-flight download has got, so a caller joining one already in progress can draw
// the bar where it actually is rather than restarting it from zero.
const progressCache = new Map();

// Everyone currently watching a download, keyed by instrument id. A download belongs to the
// library rather than to whoever happened to start it, so a modal that closes and reopens midway
// keeps getting updates instead of watching a frozen bar.
const progressListeners = new Map();

const emitProgress = (instrumentId, loaded, total) => {
  progressCache.set(instrumentId, { loaded, total });

  const listeners = progressListeners.get(instrumentId);

  if (listeners) listeners.forEach((listener) => listener(loaded, total));
};

const addProgressListener = (instrumentId, listener) => {
  if (!progressListeners.has(instrumentId)) progressListeners.set(instrumentId, new Set());

  progressListeners.get(instrumentId).add(listener);
};

/**
 * Watch a download without starting one.
 *
 * The picker and the play button can both set a download going, so whoever wants to draw a
 * progress bar can't be the one who owns it. Subscribing separately means the bar shows the
 * download either of them started — including one already running when the subscriber turned up,
 * which is why the current figures are replayed immediately.
 *
 * @param {function} listener - Called with (loaded, total) as each file lands, and with a third
 *   argument of true if the download fails
 * @returns {function} unsubscribe
 */
export function subscribeToProgress(instrumentId, listener) {
  if (settledIds.has(instrumentId)) return () => {};

  const progress = progressCache.get(instrumentId);

  if (progress) listener(progress.loaded, progress.total);

  addProgressListener(instrumentId, listener);

  return () => {
    const listeners = progressListeners.get(instrumentId);

    if (listeners) listeners.delete(listener);
  };
}

/** Whether a download for this instrument is running right now, started by anyone. */
export const isInstrumentLoading = (instrumentId) =>
  bufferCache.has(instrumentId) && !settledIds.has(instrumentId);

const getSampleUrl = (preset, note) => `${SOUNDFONT_BASE_URL}/${preset.patch}-mp3/${note}.mp3`;

// Where a sustaining instrument's recording loops, in seconds into the file.
//
// Every note in the FluidR3 set is the same fixed-length render — a hair over 3.1 seconds, cut
// dead at the end with no fade — so a held organ or pad note simply stops mid-note, at full
// level, a few seconds in. Looping a stretch of the steady part is what lets it hold for as long
// as the clip asks. The window starts well after the attack has settled and turns back before the
// last frames, where an MP3 decoder may leave padding of its own.
const LOOP_START_SECONDS = 1.5;
const LOOP_TURN_SECONDS = 2.8;

// How far either end of the loop may move to land on a turning point in the waveform. Searching
// ten milliseconds either side spans a full cycle of anything down to 50Hz, so every note in the
// range a sustaining instrument is sampled over has one to find — the lowest, a contrabass E1,
// included.
const MIRROR_SEARCH_SECONDS = 0.01;

// The loop points of the recording they belong to, stashed on the buffer itself. They can't be
// worked out from constants at playback: both ends are nudged onto a turning point in that
// particular waveform, which is a property of the recording and different for every note.
const SUSTAIN_LOOP = 'sustainLoop';

/** Where a recording loops, or null for one that isn't looped. */
export const getSampleLoop = (buffer) => buffer?.[SUSTAIN_LOOP] || null;

/**
 * The sample nearest `centre` where the waveform turns around, i.e. where its slope is least.
 *
 * Every channel votes, because the loop is one position in the buffer and they all have to use
 * it. That is a compromise on a genuinely stereo recording, but the alternative — a different
 * loop per channel — is not something a single AudioBufferSourceNode can do.
 */
const findTurningPoint = (channels, centre, search) => {
  let best = centre;
  let bestSlope = Infinity;

  for (let index = centre - search; index < centre + search; index += 1) {
    const slope = channels.reduce((total, samples) => total + Math.abs(samples[index + 1] - samples[index - 1]), 0);

    if (slope < bestSlope) {
      best = index;
      bestSlope = slope;
    }
  }

  return best;
};

/**
 * Extend a recording so it can be held indefinitely, by having it double back on itself.
 *
 * The obvious way to loop a sample is to crossfade its end into its start, and for this material
 * it is the wrong one: a chorused pad two seconds apart from itself is a different waveform, so
 * the crossfade sums two decorrelated copies and comb-filters them. That is heard as a phasey
 * swell every time round the loop — noise arriving out of nowhere once a note is held past the
 * length of its recording.
 *
 * Mirroring instead never sums anything. The buffer is rewritten as the recording up to the turn
 * followed by the same stretch backwards, and looping the whole of that plays the material
 * forwards, then in reverse, then forwards again — every sample of it exactly as recorded. Both
 * joins are a mirror about one sample, so they cannot jump; landing each one on a turning point,
 * where the slope is near zero, means they cannot corner either. Reversal is inaudible on a
 * sustained pad, organ or string, which is the only kind of preset this runs on.
 *
 * @returns {{ buffer: AudioBuffer, loop: { start: number, end: number } }} in seconds, or null
 *   when the recording is too short to hold the window — falling back to no loop plays the note
 *   exactly as it did before rather than looping a window running off the end of the buffer.
 */
const buildMirroredLoop = (Tone, audioBuffer) => {
  if (audioBuffer.duration <= LOOP_TURN_SECONDS) return null;

  const { sampleRate, numberOfChannels } = audioBuffer;
  const channels = Array.from({ length: numberOfChannels }, (unused, channel) => audioBuffer.getChannelData(channel));
  const search = Math.round(MIRROR_SEARCH_SECONDS * sampleRate);

  const start = findTurningPoint(channels, Math.round(LOOP_START_SECONDS * sampleRate), search);
  const turn = findTurningPoint(channels, Math.round(LOOP_TURN_SECONDS * sampleRate), search);

  // The attack up to the turn, then back down to the loop start. Whatever the recording had after
  // the turn is dropped: it is never reached, and it is the part an MP3 decoder is least sure of.
  const length = turn + 1 + (turn - start);
  const mirrored = Tone.getContext().createBuffer(numberOfChannels, length, sampleRate);

  channels.forEach((samples, channel) => {
    const written = mirrored.getChannelData(channel);

    written.set(samples.subarray(0, turn + 1));

    for (let offset = 0; offset < turn - start; offset += 1) {
      written[turn + 1 + offset] = samples[turn - 1 - offset];
    }
  });

  // The loop runs to the very end of what was just built, so playback turns around at `turn` and
  // wraps back to `start` — both of them the mirror points chosen above
  return { buffer: mirrored, loop: { start: start / sampleRate, end: length / sampleRate } };
};

/**
 * The Cache API, or null when it isn't usable.
 *
 * It needs a secure context — fine on localhost and in production, but not something to assume.
 * Every call site treats a null cache as "just fetch", so caching is a bonus rather than a
 * dependency.
 */
const openCache = async () => {
  try {
    if (typeof caches === 'undefined') return null;

    return await caches.open(CACHE_NAME);
  } catch (error) {
    return null;
  }
};

/**
 * One note's worth of audio, from the cache when it's there and the network when it isn't.
 *
 * Decoding runs on Tone's context even while it is suspended — decodeAudioData doesn't need the
 * context to be running — so samples can download before the user has made the gesture that
 * unblocks playback.
 *
 * A sustaining instrument gets its loop built in here, once, rather than every time a voice is
 * built: the decoded buffer is this module's own and lives as long as the instrument does, so the
 * work — and the extra second or so of audio the mirror adds — is paid for on the download and
 * never again.
 */
const loadSampleBuffer = async (Tone, cache, url, sustains) => {
  let response = cache ? await cache.match(url).catch(() => null) : null;

  if (!response) {
    response = await fetch(url);

    if (!response.ok) throw new Error(`Sample request failed (${response.status}) for ${url}`);

    // Stored before the body is read, since a Response body can only be consumed once
    if (cache) await cache.put(url, response.clone()).catch(() => {});
  }

  const audioBuffer = await Tone.getContext().decodeAudioData(await response.arrayBuffer());
  const mirrored = sustains ? buildMirroredLoop(Tone, audioBuffer) : null;

  const buffer = new Tone.ToneAudioBuffer(mirrored ? mirrored.buffer : audioBuffer);

  if (mirrored) buffer[SUSTAIN_LOOP] = mirrored.loop;

  return buffer;
};

/**
 * Work through a list of notes a few at a time, reporting each one as it lands.
 *
 * Several workers pull from the same shared index rather than the list being sliced up front, so
 * a slow file holds up only its own worker instead of a whole batch.
 */
const loadAllSamples = async (Tone, cache, preset, notes, onProgress) => {
  const buffers = {};
  const sustains = isSustainedInstrument(preset);
  let nextIndex = 0;
  let loaded = 0;

  const worker = async () => {
    while (nextIndex < notes.length) {
      const { note } = notes[nextIndex++];

      buffers[note] = await loadSampleBuffer(Tone, cache, getSampleUrl(preset, note), sustains);

      loaded += 1;
      onProgress(loaded, notes.length);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(MAX_CONCURRENT_FETCHES, notes.length) }, worker),
  );

  return buffers;
};

/**
 * Every sample for an instrument, decoded and ready to hand to a Tone.Sampler.
 *
 * @param {object} Tone - The loaded Tone module
 * @param {string} instrumentId - A sampled preset's id
 * @param {function} onProgress - Called with (loaded, total) as each file lands, and with a third
 *   argument of true if the download fails
 * @returns {Promise<Record<string, object>>} note name to ToneAudioBuffer
 * @throws when the preset isn't a sampled one, or any file fails to download
 */
export function loadInstrumentBuffers(Tone, instrumentId, onProgress = () => {}) {
  const cached = bufferCache.get(instrumentId);

  if (cached) {
    // Whether this download is finished or still running, the joining caller needs to be told
    // where it stands — otherwise its bar sits at zero until the very last file lands
    const progress = progressCache.get(instrumentId);

    if (progress) onProgress(progress.loaded, progress.total);
    if (!settledIds.has(instrumentId)) addProgressListener(instrumentId, onProgress);

    return cached;
  }

  const preset = getInstrument(instrumentId);

  if (!isSampledInstrument(preset)) {
    return Promise.reject(new Error(`${instrumentId} is not a sampled instrument`));
  }

  const notes = getSampleNotes(preset);

  addProgressListener(instrumentId, onProgress);

  // Published before the first file lands so a joiner knows how many to expect
  emitProgress(instrumentId, 0, notes.length);

  const pending = (async () => {
    const cache = await openCache();

    // The last tick is deliberately held back rather than passed straight through. Reaching
    // loaded === total is what flips a watcher to "ready", and the picker reads getDownloadedIds()
    // the moment it sees that — so announcing it here, with the marker still unwritten and the id
    // not yet in settledIds, has the picker re-read a set that does not contain this instrument
    // and leave the download size on screen for something already downloaded. It only cleared
    // when something else moved the status later, which is why switching instrument fixed it.
    const buffers = await loadAllSamples(Tone, cache, preset, notes, (loaded, total) => {
      if (loaded < total) emitProgress(instrumentId, loaded, total);
    });

    // Only now, with every note in hand, is the instrument genuinely available offline
    if (cache) {
      await cache
        .put(`${COMPLETE_MARKER_PREFIX}${preset.id}`, new Response(String(notes.length)))
        .catch(() => {});
    }

    settledIds.add(instrumentId);

    // Everything a watcher needs is true before it is told, so the re-read it does on "ready"
    // finds the instrument downloaded
    emitProgress(instrumentId, notes.length, notes.length);
    progressListeners.delete(instrumentId);

    return buffers;
  })();

  bufferCache.set(instrumentId, pending);

  // Dropped on failure so a retry actually retries rather than re-awaiting the same rejection
  pending.catch(() => {
    // Watchers are told before they're dropped, or a download play started and lost leaves their
    // bar, and the play button waiting on it, stuck at loading
    const progress = progressCache.get(instrumentId) || { loaded: 0, total: 0 };

    progressListeners.get(instrumentId)?.forEach((listener) => listener(progress.loaded, progress.total, true));

    bufferCache.delete(instrumentId);
    progressCache.delete(instrumentId);
    progressListeners.delete(instrumentId);
  });

  return pending;
}

/** Whether an instrument's samples are already decoded in memory, i.e. play will be instant. */
export const isInstrumentLoaded = (instrumentId) => settledIds.has(instrumentId);

/**
 * The markers left by earlier sessions, scanned once for the life of the page.
 *
 * The scan is one cache lookup per sampled instrument, and there are ~120 of them. It used to run
 * on every call, which is once per mounted useSampleLoader per status change — and ViewSongPart
 * mounts a modal, and with it a hook, for every part in the song at page load. A twenty-part song
 * was doing a couple of thousand lookups each time a download changed state.
 *
 * Caching the promise is safe because nothing outside this module writes a marker, and the ids
 * that gain one afterwards are exactly the ones landing in `settledIds` — which getDownloadedIds
 * unions in below, so a stale scan can never hide a download that has since finished.
 */
let markerScan = null;

const scanCompletionMarkers = async () => {
  const cache = await openCache();

  if (!cache) return null;

  const found = new Set();

  await Promise.all(
    ALL_INSTRUMENTS.filter(isSampledInstrument).map(async ({ id }) => {
      const marker = await cache.match(`${COMPLETE_MARKER_PREFIX}${id}`).catch(() => null);

      if (marker) found.add(id);
    }),
  );

  return found;
};

/**
 * Which instruments have been downloaded in full at some point, in this session or an earlier one.
 *
 * Drives dropping the download size from the picker once it no longer applies. Read from the
 * completion markers rather than by counting files, and resolves to just the in-memory set when
 * the cache is unusable — in that case sizes stay on screen, which is honest: without a cache the
 * download really would happen again.
 */
export async function getDownloadedIds() {
  if (!markerScan) markerScan = scanCompletionMarkers();

  const fromMarkers = await markerScan;

  // A failed scan isn't cached as "nothing is downloaded" — dropping it lets the next caller try
  // again, which matters when the cache was momentarily unavailable rather than absent
  if (!fromMarkers) {
    markerScan = null;

    return new Set(settledIds);
  }

  return new Set([...fromMarkers, ...settledIds]);
}
